/**
 * Patient settings toggle for en-route location checks (PROMPT 10).
 *
 * DISCLOSURE before opt-out: forfeiting misdirection-report eligibility (§10).
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ConfirmDialog from "components/common/ConfirmDialog";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/functions";
import { useAuth } from "hooks/useAuth";

const CONSENT_DOC = "enRouteLocationChecks";

const OPT_OUT_DISCLOSURE =
  "Turning this off means you will not be able to file a coverage-misdirection " +
  "report if a facility's network participation looks wrong. En-route location " +
  "checks are also how we confirm you were present. Continue only if you understand " +
  "you are giving up that complaint path.";

const EnRouteLocationConsentToggle = () => {
  const { user } = useAuth();
  const [granted, setGranted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return undefined;
    }
    setLoadFailed(false);
    const ref = firebase
      .firestore()
      .collection("users")
      .doc(user.uid)
      .collection("consents")
      .doc(CONSENT_DOC);
    return ref.onSnapshot(
      (snap) => {
        const data = snap.exists ? snap.data() : null;
        setGranted(
          Boolean(data?.grantedAt) &&
            !data?.revokedAt &&
            data?.granted !== false,
        );
        setLoadFailed(false);
        setLoading(false);
      },
      (err) => {
        setLoadFailed(true);
        setError(
          err?.message ||
            "Unable to load en-route consent status — do not assume it is off.",
        );
        setLoading(false);
      },
    );
  }, [user?.uid]);

  const persist = async (nextGranted) => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const callable = firebase
        .functions()
        .httpsCallable("updateEnRouteLocationConsent");
      await callable({ granted: nextGranted });
      setGranted(nextGranted);
      setSuccess(
        nextGranted
          ? "En-route location checks enabled."
          : "En-route location checks turned off. Misdirection reports are unavailable while opted out.",
      );
    } catch (err) {
      setError(err?.message || "Could not update consent.");
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const onToggle = (_event, checked) => {
    if (!checked && granted) {
      setConfirmOpen(true);
      return;
    }
    persist(true);
  };

  if (!user?.uid) return null;

  return (
    <Box data-testid="careverify-enroute-consent">
      <Typography variant="subtitle1" gutterBottom>
        En-route location checks
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        While you are traveling to a booked visit, CareVerify may request your
        location about every three minutes (one-shot, not continuous tracking)
        to update your ETA and confirm arrival. This is separate from other
        location features in the app.
      </Typography>
      {error ? (
        <Alert
          severity="error"
          onClose={loadFailed ? undefined : () => setError("")}
          sx={{ mb: 1 }}
        >
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 1 }}>
          {success}
        </Alert>
      ) : null}
      <FormControlLabel
        control={
          <Switch
            checked={granted}
            onChange={onToggle}
            disabled={loading || saving || loadFailed}
            inputProps={{ "aria-label": "En-route location checks" }}
          />
        }
        label={
          loadFailed
            ? "Status unavailable"
            : granted
              ? "Enabled"
              : "Off"
        }
      />
      {!loadFailed && !granted ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          With this off, you cannot file a coverage-misdirection complaint.
        </Alert>
      ) : null}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Turn off en-route location checks?"
        message={OPT_OUT_DISCLOSURE}
        confirmLabel="Turn off"
        confirmColor="error"
        loading={saving}
        onConfirm={() => persist(false)}
      />
      {saving ? (
        <Box sx={{ mt: 1 }}>
          <LoadingButton loading disabled>
            Saving
          </LoadingButton>
        </Box>
      ) : null}
    </Box>
  );
};

export default EnRouteLocationConsentToggle;
